<template>
  <div>
    <!-- Edit mode with latitude/longitude inputs -->
    <v-row v-if="edit" no-gutters>
      <!-- Latitude input -->
      <v-col class="pr-2">
        <v-text-field
          :ref="refKey"
          solo
          dense
          clearable
          prepend-icon="mdi-latitude"
          v-model="latitudeValue"
          @input="onCoordinateChange"
          :rules="[rules.notEmpty, rules.validNumber]"
          label="Latitude"
        >
          <!-- Append Undo Button with Tooltip -->
          <template v-slot:append>
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" @click="onUndo">
                  mdi-undo
                </v-icon>
              </template>
              <span>{{ undoLiteral.value || 'blank' }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </v-col>

      <!-- Longitude input -->
      <v-col>
        <v-text-field
          solo
          dense
          append-outer-icon="mdi-delete-outline"
          prepend-icon="mdi-longitude"
          v-model="longitudeValue"
          @input="onCoordinateChange"
          @click:append-outer="onDelete"
          :rules="[rules.notEmpty, rules.validNumber]"
          label="Longitude"
        ></v-text-field>
      </v-col>
    </v-row>

    <!-- Display mode with tooltip showing raw JSON array -->
    <div v-else class="d-inline-flex align-center">
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <span v-on="on" v-bind="attrs">
            <span class="text-caption">
              lat
              <highlighted-text
                class="text-body-1"
                :value="displayLatitude"
                :highlights="highlights"
              ></highlighted-text
              >&nbsp;
            </span>
            <span class="text-caption">
              long
              <highlighted-text
                class="text-body-1"
                :value="displayLongitude"
                :highlights="highlights"
              ></highlighted-text>
            </span>
          </span>
        </template>
        <span>{{ 'raw ' + literal.value }}</span>
      </v-tooltip>
      <copy-icon :value="literal.value" />
    </div>
  </div>
</template>

<script>
import HighlightedText from './HighlightedText.vue'
import CopyIcon from './CopyIcon.vue'
import { Literal } from '@/rec-cache/rec-cache-types'

/**
 * GeoCoordinateValue Component - Handles rec:coordinates properties
 *
 * DATA FORMAT:
 * -----------
 * Storage: Coordinates stored as JSON array: [longitude, latitude]
 * Display: Shows as "lat Y long X" for better readability
 * Edit: Two separate input fields for latitude and longitude
 *
 * NOTE: The JSON array format is [longitude, latitude] (lon first, lat second)
 * but we display it as "lat X long Y" for better UX
 */

export default {
  components: { HighlightedText, CopyIcon },
  props: {
    literal: {
      type: Object,
      default: () => new Literal(),
    },
    highlights: {
      type: [String, Array],
      default: () => [],
    },
    edit: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    latitudeValue: '',
    longitudeValue: '',
    undoLiteral: new Literal(),
    rules: {
      notEmpty: (value) => !!value || 'Value must be defined!',
      validNumber: (value) => {
        if (!value) return true
        const num = parseFloat(value)
        return !isNaN(num) || 'Must be a valid number!'
      },
    },
    refKey: '',
  }),
  computed: {
    // Display latitude from stored [longitude, latitude] array
    displayLatitude: function () {
      if (!this.literal.value) return ''
      try {
        const [, lat] = JSON.parse(this.literal.value)
        return lat.toString()
      } catch (error) {
        return this.literal.value
      }
    },
    // Display longitude from stored [longitude, latitude] array
    displayLongitude: function () {
      if (!this.literal.value) return ''
      try {
        const [lon] = JSON.parse(this.literal.value)
        return lon.toString()
      } catch (error) {
        return this.literal.value
      }
    },
  },
  watch: {
    literal: {
      handler(newValue) {
        this.parseCoordinateValue(newValue.value)
      },
      immediate: true,
    },
    edit: function (newValue) {
      if (newValue) {
        this.undoLiteral = this.literal.clone()
        this.autoFocus()
      }
    },
  },
  created: function () {
    this.parseCoordinateValue(this.literal.value)
    this.refKey = Math.random().toString(36).slice(2, 8)
  },
  mounted() {
    this.autoFocus()
  },
  methods: {
    parseCoordinateValue(value) {
      // Parses stored JSON array [longitude, latitude] and extracts values for editing
      if (!value) {
        this.latitudeValue = ''
        this.longitudeValue = ''
        return
      }

      try {
        const [lon, lat] = JSON.parse(value)
        this.latitudeValue = lat.toString()
        this.longitudeValue = lon.toString()
      } catch (error) {
        console.error('Error parsing geo coordinates:', error)
        this.latitudeValue = ''
        this.longitudeValue = ''
      }
    },
    onCoordinateChange() {
      // Combine latitude and longitude into JSON array format [longitude, latitude]
      if (this.latitudeValue && this.longitudeValue) {
        const lat = parseFloat(this.latitudeValue)
        const lon = parseFloat(this.longitudeValue)

        if (!isNaN(lat) && !isNaN(lon)) {
          // Store as [longitude, latitude] array (note the order!)
          const value = JSON.stringify([lon, lat])
          // Preserve the original datatype (could be rdfJSON or xsdString)
          this.$emit('input', new Literal(value, this.literal.datatype))
        }
      }
    },
    autoFocus() {
      this.$nextTick(() => {
        if (!this.latitudeValue && this.$refs[this.refKey]) {
          this.$refs[this.refKey].focus()
        }
      })
    },
    onUndo: function () {
      this.$emit('input', this.undoLiteral)
    },
    onDelete: function () {
      this.$emit('delete', this.literal)
    },
  },
}
</script>

<style scoped>
/* Match the styling from DateTimeValue and LiteralValue */
</style>
